import { useEffect, useRef, useState } from 'react';
import { placesApi, type AutocompletePrediction } from '../../../api/places.api';

// New session token per destination search — reused across keystrokes and
// consumed by the paired details lookup, so the whole search bills as ONE
// Places session (ADR-0001).
function genSessionToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** "Lisboa, Portugal" — the readable destination, not a postal address. */
export function predictionLabel(p: AutocompletePrediction): string {
  return p.secondaryText ? `${p.mainText}, ${p.secondaryText}` : p.mainText;
}

function normalizeForMatch(text: string): string {
  return text.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

type Resolve = (destination: string, coords?: { lat: number; lng: number }) => void;

/**
 * Autocomplete goes through the server proxy (placesApi) — debounced, session-
 * tokened, cached and cost-capped server-side — rather than hitting the Google
 * JS SDK directly from the browser.
 */
export function useDestinationSearch(query: string, active: boolean, resolve: Resolve) {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const tokenRef = useRef<string>(genSessionToken());
  // Picking a prediction rewrites the input; that rewrite must not search again.
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    if (skipNextFetchRef.current) { skipNextFetchRef.current = false; return; }
    const q = query.trim();
    if (!active || q.length < 2) { setPredictions([]); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      placesApi.autocomplete(q, tokenRef.current, { kind: 'destination' })
        .then(list => { if (!cancelled) setPredictions(list); })
        .catch(() => { if (!cancelled) setPredictions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, active]);

  // Only when the text actually changes: an unchanged value fires no effect,
  // and a stale flag would swallow the next real keystroke.
  function rewrite(label: string, coords?: { lat: number; lng: number }) {
    if (label !== query) skipNextFetchRef.current = true;
    resolve(label, coords);
  }

  async function select(prediction: AutocompletePrediction) {
    setPredictions([]);
    // The prediction already carries the readable name, so use that rather
    // than the Place Details `formattedAddress`, which for anything but a
    // plain city is a full postal address.
    rewrite(predictionLabel(prediction));
    // Details only for the coords, with the same token → one billing session.
    const detail = await placesApi.details(prediction.placeId, tokenRef.current);
    tokenRef.current = genSessionToken();
    if (detail) resolve(predictionLabel(prediction), { lat: detail.lat, lng: detail.lng });
  }

  /**
   * Moving on without touching the dropdown is fine — the server geocodes the
   * destination lazily the first time a map opens. All this does is adopt the
   * canonical spelling when what the user typed unambiguously *is* one of the
   * predictions, which costs nothing: asking Google for its coords here would
   * be dearer than that lazy geocode.
   */
  function adoptExactMatch() {
    const typed = normalizeForMatch(query);
    const exact = predictions.find(p => normalizeForMatch(p.mainText) === typed
      || normalizeForMatch(predictionLabel(p)) === typed);
    if (exact) rewrite(predictionLabel(exact));
    setPredictions([]);
  }

  return { predictions, select, adoptExactMatch };
}
